import {SettingsService} from './settings.service';
import { Injectable, inject } from '@angular/core';
import {ProjectService} from "./project.service";
import {FirebaseApi} from "../../common/services/firebase-api.service";
import {BehaviorSubject, EMPTY, Observable, combineLatest, defer, timer} from "rxjs";
import {catchError, distinctUntilChanged, map, retry, switchMap, tap} from "rxjs/operators";
import {ProjectApiService} from "../../common/services/api/routes/project-api.service";
import {SyncService} from "./sync.service";
import {ProductData, effectivePricing, effectiveDistancePricing} from "../../common/model/product-data";
import {ProductSeason, seasonInForce} from "../../common/model/product-season";
import {CommonUtil} from "../../common/utils/common.util";
import {BaseGeometry} from "../models/base-geometry";
import {Polygon} from "../models/polygon";
import {Spot} from "../models/spot";
import {AuthService} from "../../common/services/auth.service";
import {Logger} from "../../common/utils/logger";
import {PriceType} from "../../common/enums/price-type.enum";
import {UnitConverter} from "../utils/unit-converter";
import {ProductEstimate, ProductEstimateLine} from "../../common/model/product-estimate";
import {ProjectData} from "../../common/model/project-data";
import {StringsService} from "../../../../projects/common/src/lib/services/strings.service";
import {intlLocale} from "../../common/utils/intl-format";
import {countryCode} from "../../common/utils/countries";

/** Countries that price in euros. */
const EURO_COUNTRIES = ['AT', 'BE', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT',
  'NL', 'PT', 'SK', 'SI', 'ES', 'AD', 'MC', 'SM', 'VA', 'ME', 'XK', 'GF', 'GP', 'MQ', 'RE', 'YT', 'PM', 'BL', 'MF'];

const COUNTRY_CURRENCY: {[country: string]: string} = {
  US: 'usd', GB: 'gbp', CA: 'cad', AU: 'aud', NZ: 'nzd', CH: 'chf', LI: 'chf', SE: 'sek', NO: 'nok', DK: 'dkk',
  IS: 'isk', PL: 'pln', CZ: 'czk', HU: 'huf', RO: 'ron', BG: 'bgn', RS: 'rsd', UA: 'uah', RU: 'rub', TR: 'try',
  JP: 'jpy', CN: 'cny', HK: 'hkd', TW: 'twd', KR: 'krw', IN: 'inr', ID: 'idr', MY: 'myr', SG: 'sgd', TH: 'thb',
  VN: 'vnd', PH: 'php', PK: 'pkr', BD: 'bdt', AE: 'aed', SA: 'sar', QA: 'qar', IL: 'ils', EG: 'egp', ZA: 'zar',
  NG: 'ngn', KE: 'kes', MA: 'mad', BR: 'brl', MX: 'mxn', AR: 'ars', CL: 'clp', CO: 'cop', PE: 'pen', UY: 'uyu',
  PR: 'usd', EC: 'usd', SV: 'usd', PA: 'usd',
};

/** Where neither the account nor the browser says which country it is in. */
const LANGUAGE_CURRENCY: {[language: string]: string} = {
  de: 'eur', es: 'eur', fr: 'eur', pt: 'eur', ja: 'jpy', in: 'idr', id: 'idr', hi: 'inr', ru: 'rub', en: 'usd',
};

function currencyOfCountry(code: string): string {
  if (!code) {
    return '';
  }
  return EURO_COUNTRIES.includes(code) ? 'eur' : (COUNTRY_CURRENCY[code] || '');
}

@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  private settings = inject(SettingsService);
  private authService = inject(AuthService);
  private projectService = inject(ProjectService);
  private firebaseApi = inject(FirebaseApi);
  private syncService = inject(SyncService);
  private projectApi = inject(ProjectApiService);
  private strings = inject(StringsService);



  public products: ProductData[] = [];
  public productsMap = {};
  /**
   * True once the catalogue query has returned at least once.
   *
   * An empty `productsMap` is ambiguous — it means either "no products" or "not
   * loaded yet" — and denormalizing an estimate in the second case would write a
   * zero over a correct figure. A catalogue that could not be read stays not
   * loaded, for the same reason.
   */
  public productsLoaded = false;
  /**
   * True when the catalogue could not be read. Not the same as an empty one:
   * the products page told an admin their organisation had no products and
   * offered to add the first, and the product page showed a skeleton for ever.
   */
  public productsError = false;
  private productsSubject = new BehaviorSubject<ProductData[]>([]);
  /** Bumped by reload(), so the same catalogue is read again. */
  private readAttempt = 0;
  private reload$ = new BehaviorSubject<number>(0);

  get productsObservable(): Observable<ProductData[]> {
    return this.productsSubject.asObservable();
  }

  constructor() {
    // Let the Polygon model compute its on-map price label without importing
    // this service (avoids a circular dependency).
    Polygon.computePriceText = (polygon) => this.calculatePriceDisplay(polygon);
    this.init();
  }

  /** Re-render the on-map price labels — call when product prices change so the
   *  labels don't go stale until a reload. */
  refreshPriceLabels() {
    this.syncService.getPolygons().forEach(polygon => {
      if (polygon.showPriceLabel) {
        polygon.updateLabel();
      }
    });
  }

  init() {
    // Read again only when the catalogue itself changes. The project document
    // emits on every snapshot (a collaborator connecting, last_open, the list
    // summary written after each save), and each one used to empty the
    // catalogue and read it again: pickers blanked, prices flickered, and a
    // visitor's tab asked the API once more every time.
    combineLatest([this.projectService.observe, this.authService.userInfoSubject, this.reload$]).pipe(
      map(([project, , attempt]) => {
        const source = this.catalogueSource(project);
        return {...source, key: `${source.key}#${attempt}`};
      }),
      distinctUntilChanged((a, b) => a.key === b.key),
      tap(catalogue => {
        Logger.log(`ProductsService catalogue = ${catalogue.key}`);
        this.clearProducts();
      }),
      switchMap(catalogue => catalogue.read ? catalogue.read() : EMPTY),
    ).subscribe(products => this.applyProducts(products));
  }

  /** Reads the catalogue again, after it could not be read. */
  reload() {
    this.productsError = false;
    this.readAttempt += 1;
    this.reload$.next(this.readAttempt);
  }

  /** The catalogue could not be read: say so, rather than show an empty one. */
  private catalogueFailed() {
    this.productsError = true;
    this.productsSubject.next(this.products);
  }

  /**
   * Which catalogue this session prices with, under a key that changes only
   * when the catalogue does, and how to read it.
   *
   * With a project open it is the one the server prices that project from:
   * its creator's organization's, or the creator's own outside one
   * (catalogue-api, products-db catalogueOwner). It used to follow the
   * VIEWER's organization, so an organization owner opening a member's
   * project read the member's empty personal list, and a member opening an
   * outside project read their own organization's. Prices went blank and the
   * project's stored estimate was overwritten with zero on the way out.
   */
  private catalogueSource(project: ProjectData | null): {key: string, read: (() => Observable<ProductData[]>) | null} {
    const viewer = this.authService.getUserId || '';
    const projectId = project?.project_id || '';
    const owner = project ? this.projectCatalogueOwner(project) : this.catalogueUid;
    if (owner && this.canReadCatalogue(owner)) {
      return {key: `${viewer}|${projectId}|${owner}`, read: () => this.liveCatalogue(owner, projectId)};
    }
    if (projectId) {
      // Someone else's catalogue: a visitor of a shared project, a guest,
      // a collaborator from outside the organization, a colleague. The rules
      // keep price lists to their owner, and only the server can tell whose
      // organization the creator is in, so the prices this project needs come
      // from the API -- once, without internal fields such as the cost price.
      return {key: `${viewer}|${projectId}|`, read: () => this.projectCatalogue(projectId)};
    }
    return {key: viewer, read: null};
  }

  /**
   * Whose catalogue prices `project`, when this account can tell on its own;
   * null when only the server can (anyone else's project, a colleague's
   * included: a member's account does not list the organization's members).
   */
  private projectCatalogueOwner(project: ProjectData): string | null {
    const me = this.authService.getUserId;
    const creator = project.creator_id;
    if (!me || !creator) {
      return null;
    }
    if (creator === me) {
      return this.catalogueUid;
    }
    const info = this.authService.currentUserInfo;
    const myOrganization = info?.enterpriseUid?.trim() ? info.enterpriseUid : '';
    if (myOrganization) {
      // The organization owner's own project prices from the organization's.
      return creator === myOrganization ? myOrganization : null;
    }
    // An organization owner: a member's project prices from the organization's
    // catalogue, which is the owner's own.
    return (info?.enterpriseUsers ?? []).includes(creator) ? me : null;
  }

  /** A catalogue this session may read directly, kept live. */
  private liveCatalogue(owner: string, projectId: string): Observable<ProductData[]> {
    // One catalogue: the organization's inside an organization, the user's
    // own otherwise. Members used to see the organization's list merged with
    // their own products, and nobody could tell the two apart; the server
    // reads the same single source (products-db).
    return this.firebaseApi.getUserProductsRef(owner).valueChanges().pipe(
      map(list => list ?? []),
      catchError(err => {
        Logger.error('Catalogue not readable directly', err);
        if (projectId) {
          return this.projectCatalogue(projectId);
        }
        this.catalogueFailed();
        return EMPTY;
      }));
  }

  /** Empties the catalogue when the one in use is about to be replaced. */
  private clearProducts() {
    this.productsError = false;
    if (!this.productsLoaded && this.products.length === 0) {
      return;
    }
    this.products = [];
    this.productsMap = {};
    this.productsLoaded = false;
    this.productsChanged();
  }

  /** Tells the pickers, the textures and the measurements box. */
  private productsChanged() {
    this.productsSubject.next(this.products);
    // The box works its estimate out when shapes change, so it stayed blank
    // after a project opened, until the next edit, if the prices came later.
    if (this.projectService.currentProject) {
      this.syncService.updateMeasurementBox.emit();
    }
  }

  /**
   * Whether the Firestore rules let this session read `ownerUid`'s catalogue
   * live: its own, its organization's, or (for an organization owner) a
   * member's.
   */
  private canReadCatalogue(ownerUid: string): boolean {
    if (!this.authService.isRealUser()) {
      return false;
    }
    const info = this.authService.currentUserInfo;
    return ownerUid === this.authService.getUserId
      || (!!info?.enterpriseUid && info.enterpriseUid === ownerUid)
      || (info?.enterpriseUsers ?? []).includes(ownerUid);
  }

  private projectCatalogue(projectId: string): Observable<ProductData[]> {
    // Deferred so that each retry sends the request again.
    return defer(() => this.projectApi.getProjectProducts(projectId)).pipe(
      // A dropped connection or a busy server is usually gone moments later.
      retry({count: 2, delay: (err, attempt) => timer(attempt * 2000)}),
      map(products => products ?? []),
      catchError(err => {
        // Unavailable is not empty. Answering with an empty list marked the
        // catalogue loaded, and leaving the project then wrote a zero
        // estimate over the real one.
        Logger.error('Project catalogue unavailable', err);
        this.catalogueFailed();
        return EMPTY;
      }));
  }

  private applyProducts(products: ProductData[]) {
    Logger.log(`ProductsService onProjectUpdate products: `, products);
    this.products = products;
    // Rebuild the map from scratch — otherwise a product deleted in the
    // dashboard lingers here and keeps pricing geometries that reference it.
    this.productsMap = {};
    products.forEach(product => {
      this.productsMap[product.id] = product;
    });
    this.productsLoaded = true;
    this.productsError = false;
    this.productsChanged();
    // A product's price may have changed in the dashboard — refresh the
    // on-map price labels so they don't go stale until a reload.
    this.refreshPriceLabels();
  }

  /**
   * Whose product catalogue applies. Enterprise members all share the
   * organization owner's catalogue, so a team quotes one consistent set of
   * prices instead of every member maintaining their own private list.
   */
  private catalogueOwnerUid(fallbackUid: string): string {
    const enterpriseUid = this.authService.currentUserInfo?.enterpriseUid;
    return enterpriseUid && enterpriseUid.trim().length > 0 ? enterpriseUid : fallbackUid;
  }

  /**
   * True when the catalogue is an organization's: for its members, and for
   * the owner of an Enterprise plan, whose own catalogue IS the
   * organization's.
   */
  get isOrganizationCatalogue(): boolean {
    const enterpriseUid = this.authService.currentUserInfo?.enterpriseUid;
    return (!!enterpriseUid && enterpriseUid.trim().length > 0) || this.authService.hasEnterpriseSubscription;
  }

  /** Where this user's products are kept: the organization owner's uid in an organization, their own otherwise. */
  get catalogueUid(): string {
    return this.catalogueOwnerUid(this.authService.getUserId);
  }

  getDefaultProductId() {
    return this.settings.data.defaultProductId;
  }

  /** Products offerable for assignment — excludes archived (soft-deleted) and
   *  disabled products. Archived products stay in productsMap so they still
   *  price existing geometries that reference them. */
  get assignableProducts(): ProductData[] {
    return this.products.filter(p => !p.archived && p.enabled !== false);
  }

  /** The currency most products use — drives the default currency for new
   *  products so a project naturally stays single-currency (no FX conversion
   *  is ever applied; mixed currencies are surfaced as a warning in estimates). */
  dominantCurrency(): string {
    this.refreshPricingDate();
    const counts: { [c: string]: number } = {};
    this.products.forEach(p => {
      const c = this.productCurrency(p);
      if (c) { counts[c] = (counts[c] || 0) + 1; }
    });
    let best: string = null;
    Object.keys(counts).forEach(c => { if (best === null || counts[c] > counts[best]) { best = c; } });
    return best || this.homeCurrency();
  }

  /**
   * The currency a first product starts in: the one of the account's billing
   * country, else of the browser's region, else of the app language. It was
   * always US dollars, so a Spanish account changed every product by hand
   * until most of the catalogue was in euros.
   */
  homeCurrency(): string {
    const info = this.authService.currentUserInfo;
    const fromCountry = currencyOfCountry(countryCode(info?.billingInfo?.country || info?.country));
    if (fromCountry) {
      return fromCountry;
    }
    let region = '';
    try {
      region = (navigator.language || '').split('-')[1] || '';
    } catch {
      region = '';
    }
    return currencyOfCountry(region.toUpperCase())
      || LANGUAGE_CURRENCY[('' + this.strings.getLanguage()).toLowerCase()]
      || 'usd';
  }


  hasProducts(){
    if(this.products.length > 0) {
      return this.pricedGeometries().some(geo => this.productsOf(geo).length > 0);
    }
    return false
  }

  /**
   * The products on a shape that this catalogue holds, in the shape's order,
   * each once. A shape carries several when its work is layered: a car park
   * is asphalt, a sealing coat and line paint, each priced on the same area.
   */
  productsOf(geo: BaseGeometry): ProductData[] {
    const ids = (geo && geo.productIds) || [];
    return ids.map(id => this.productsMap[id] as ProductData).filter(product => !!product);
  }

  /** All priceable geometries (polygons + spots). */
  private pricedGeometries(): BaseGeometry[] {
    return [...this.syncService.getPolygons(), ...this.syncService.getSpots()];
  }

  calculateTotalPrice() {
    if(this.products.length > 0) {
      this.refreshPricingDate();
      let total = 0;
      this.pricedGeometries().forEach(geo => {
        total += this.calculatePrice(geo);
      });

      return total;
    }
    return 0;
  }
  calculateTotalPriceText() {
    if(this.products.length > 0) {
      const estimate = this.getEstimate();
      // With nothing on the map given a product there is no estimate: the
      // measurements box said "Estimate $0.00".
      if (!estimate.lines.length) {
        return '';
      }
      // One figure per currency, the largest first. No exchange rate is ever
      // applied, so adding a EUR line to a USD one gave a number that was
      // neither, labelled with whichever currency the first shape used.
      const currencies = Object.keys(estimate.subtotals)
        .sort((a, b) => estimate.subtotals[b] - estimate.subtotals[a]);
      const priced = currencies.filter(c => estimate.subtotals[c] > 0);
      return (priced.length ? priced : [estimate.currency])
        .map(c => this.formatPrice(estimate.subtotals[c] || 0, c))
        .join(' + ');
    }
    return '';
  }

  /** Per-geometry cost. Holes are never priced (they belong to the parent) and
   *  area pricing only applies to surface polygons; distance pricing applies to
   *  the perimeter (so polylines can still be length-priced). */
  /**
   * The date every price is resolved against.
   *
   * A single seam so an estimate cannot price half its lines in one season and
   * half in another when the clock crosses midnight mid-calculation, and so a
   * future "price this quote as of <date>" feature has one place to set.
   * It follows today's date through refreshPricingDate().
   */
  pricingDate: Date = new Date();

  /**
   * Moves the pricing date on when the calendar has.
   *
   * Called as each pricing pass starts (an estimate, a price label, a rate),
   * never inside one, so a pass still prices every line on one date. The date
   * used to be fixed when the service was created, so a tab left open
   * overnight into a new season kept quoting, and saving, the old rates.
   */
  private refreshPricingDate() {
    const now = new Date();
    if (now.toDateString() !== this.pricingDate.toDateString()) {
      this.pricingDate = now;
    }
  }

  /** Area pricing in force for this product on the pricing date. */
  private pricingOf(product: ProductData) {
    return effectivePricing(product, this.pricingDate);
  }

  /** Distance pricing in force for this product on the pricing date. */
  private distancePricingOf(product: ProductData) {
    return effectiveDistancePricing(product, this.pricingDate);
  }

  /** What a shape costs: each of its products, added up (one currency or not -- see geoTotals). */
  private geoTotal(geo: BaseGeometry): number {
    return this.productsOf(geo).reduce((sum, product) => sum + this.productCost(geo, product), 0);
  }

  /** What a shape costs, per currency: its products need not all be priced in one. */
  geoTotals(geo: BaseGeometry): {[currency: string]: number} {
    const totals: {[currency: string]: number} = {};
    this.productsOf(geo).forEach(product => {
      const currency = (this.productCurrency(product) || 'usd').toUpperCase();
      totals[currency] = (totals[currency] || 0) + this.productCost(geo, product);
    });
    return totals;
  }

  /** What one product on a shape costs: by area and length for a shape, once for a spot. */
  productCost(geo: BaseGeometry, product: ProductData): number {
    if (!product) {
      return 0;
    }
    const pricing = this.pricingOf(product);
    const distancePricing = this.distancePricingOf(product);
    let total = 0;
    if (geo instanceof Polygon) {
      const poly = geo as any;
      if (poly.isHole) {
        return 0;
      }
      if (poly.showSurface !== false) {
        total += CommonUtil.calculateEstimatePrice(pricing, geo.area);
      }
      if (distancePricing) {
        total += CommonUtil.calculateEstimatePrice(distancePricing, geo.perimeter);
      }
    } else if (geo instanceof Spot) {
      // A marker is one of something -- a tree, a bollard, a light. Its own
      // per-item price when the product has one; otherwise, as before, the
      // area rate once (count = 1).
      total += CommonUtil.calculateCountPrice(product.itemPricing || pricing, 1);
    }
    return total;
  }

  private productCurrency(product: ProductData): string {
    const pricing = this.pricingOf(product);
    const distancePricing = this.distancePricingOf(product);
    return (pricing && pricing.currency) || (distancePricing && distancePricing.currency)
      || (product?.itemPricing && product.itemPricing.currency);
  }

  calculatePrice(geo: BaseGeometry){
    return this.geoTotal(geo);
  }

  calculatePriceDisplay(geo: BaseGeometry){
    if (!this.productsOf(geo).length) {
      return '';
    }
    this.refreshPricingDate();
    // One figure per currency, never added across them.
    const totals = this.geoTotals(geo);
    return Object.keys(totals).map(currency => this.formatPrice(totals[currency], currency)).join(' + ');
  }

  /** One product's price on a shape, formatted in its own currency. */
  productCostDisplay(geo: BaseGeometry, product: ProductData): string {
    this.refreshPricingDate();
    return this.formatPrice(this.productCost(geo, product), this.productCurrency(product));
  }

  /**
   * A price written the app language's way ("1.234,50 €" in German), like
   * the measurements beside it and the Estimate dialog. Every price here was
   * en-US, so the estimate box read "€1,234.50" and the breakdown it opens
   * "1.234,50 €" -- in German, a comma is the decimal mark.
   */
  formatPrice(amount: number, currency: string): string {
    return CommonUtil.formatPrice(amount, currency, 2, intlLocale(this.strings.getLanguage()));
  }

  /** The rate shown to the user — the one in force today, not the base rate. */
  getProductPricing(product: ProductData) {
    this.refreshPricingDate();
    const parts: string[] = [];
    const pricing = this.pricingOf(product);
    const distancePricing = this.distancePricingOf(product);
    // Area pricing (optional — a product can be distance-only)
    if (pricing) {
      if (pricing.type === PriceType.RANGE) {
        parts.push(this.bandsLabel(pricing.prices, pricing.currency, pricing.unit));
      } else if (pricing.type === PriceType.FIXED) {
        parts.push(`${this.formatPrice(pricing.price, pricing.currency)}/${pricing.unit}`);
      }
    }
    // Distance pricing
    if (distancePricing) {
      if (distancePricing.type === PriceType.RANGE) {
        parts.push(this.bandsLabel(distancePricing.prices, distancePricing.currency, distancePricing.unit));
      } else if (distancePricing.type === PriceType.FIXED) {
        parts.push(`${this.formatPrice(distancePricing.price, distancePricing.currency)}/${distancePricing.unit}`);
      }
    }
    // Per marker
    if (product?.itemPricing) {
      parts.push(this.strings.getString('price_each', undefined,
        this.formatPrice(product.itemPricing.price, product.itemPricing.currency)));
    }
    return parts.join(' + ') || '—';
  }

  /**
   * Price bands as "lowest - highest/unit", or one price when every band
   * costs the same. It read only the first and last band ("$40.00 - $40.00"),
   * missing a middle band priced outside them.
   */
  private bandsLabel(prices: {price: number}[], currency: string, unit: string): string {
    const values = (prices || []).map(p => p.price).filter(v => typeof v === 'number' && isFinite(v));
    if (!values.length) {
      return '—';
    }
    const low = Math.min(...values);
    const high = Math.max(...values);
    return low === high
      ? `${this.formatPrice(low, currency)}/${unit}`
      : `${this.formatPrice(low, currency)} - ${this.formatPrice(high, currency)}/${unit}`;
  }

  /** The season in force today for this product, if any — for a badge. */
  activeSeasonName(product: ProductData): string {
    return this.seasonInForceFor(product)?.name ?? '';
  }

  /** The season whose rate the product is charged at today, or null for its usual rate. */
  seasonInForceFor(product: ProductData): ProductSeason | null {
    this.refreshPricingDate();
    return seasonInForce(product?.seasons, this.pricingDate);
  }

  /** Build an itemized project estimate, aggregated per product. */
  /**
   * @param projectTaxRate Fallback rate for products that do not set their own.
   *        Defaults to the current project's, which is what every caller wants.
   */
  getEstimate(projectTaxRate: number = this.projectService.currentProject?.tax_rate || 0): ProductEstimate {
    this.refreshPricingDate();
    const map: { [id: string]: ProductEstimateLine } = {};
    this.pricedGeometries().forEach(geo => {
      const poly = geo as any;
      if (geo instanceof Polygon && poly.isHole) {
        return;
      }
      // One line per product, whichever shapes it is on and in whatever
      // layer: the asphalt of three car parks is one line.
      this.productsOf(geo).forEach(product => {
        const productId = product.id;
        // Units come from the pricing in force too: a season may quote the same
        // product in different units (per m² in summer, per hour in winter).
        const pricing = this.pricingOf(product);
        const distancePricing = this.distancePricingOf(product);
        let line = map[productId];
        if (!line) {
          line = {
            productId,
            name: product.name || '—',
            areaQty: 0, areaUnit: pricing ? pricing.unit : '',
            distanceQty: 0, distanceUnit: distancePricing ? distancePricing.unit : '',
            count: 0, cost: 0,
            currency: (this.productCurrency(product) || 'usd').toUpperCase(),
            // A product's own rate wins; undefined (not 0) means "use the
            // project's", so a product can be explicitly zero-rated.
            taxRate: product.taxRate ?? projectTaxRate,
            tax: 0,
            product
          };
          map[productId] = line;
        }
        if (geo instanceof Polygon) {
          if (poly.showSurface !== false && pricing) {
            line.areaQty += UnitConverter.convertFromMetersTo((geo as Polygon).area, pricing.unit);
          }
          if (distancePricing) {
            line.distanceQty += UnitConverter.convertFromMetersTo((geo as Polygon).perimeter, distancePricing.unit);
          }
        }
        // Spots contribute only to the shape count + cost (per-marker pricing).
        line.cost += this.productCost(geo, product);
        line.count += 1;
      });
    });

    const lines = Object.keys(map).map(k => map[k]).sort((a, b) => b.cost - a.cost);
    const subtotals: { [c: string]: number } = {};
    const taxes: { [c: string]: number } = {};
    lines.forEach(l => {
      l.tax = l.cost * (l.taxRate || 0) / 100;
      subtotals[l.currency] = (subtotals[l.currency] || 0) + l.cost;
      taxes[l.currency] = (taxes[l.currency] || 0) + l.tax;
    });
    const currencies = Object.keys(subtotals);
    let currency = currencies[0] || 'USD';
    currencies.forEach(c => { if (subtotals[c] > (subtotals[currency] || 0)) { currency = c; } });
    return { lines, subtotals, taxes, currency, mixedCurrency: currencies.length > 1 };
  }

  /**
   * The estimate reduced to what a project list can store and show: one figure,
   * one currency, and whether that figure is hiding others.
   *
   * Returns null until the catalogue has loaded, so a caller denormalizing this
   * onto the project never writes a zero it cannot vouch for.
   *
   * Tax is included rather than left to display time: now that a product can
   * carry its own rate, the tax on a quote cannot be recovered from the
   * subtotal and the project rate alone.
   */
  getEstimateSummary(): {total: number, tax: number, currency: string, mixed: boolean} | null {
    if (!this.productsLoaded) {
      return null;
    }
    const estimate = this.getEstimate();
    return {
      total: estimate.subtotals[estimate.currency] || 0,
      tax: estimate.taxes[estimate.currency] || 0,
      currency: estimate.currency || '',
      mixed: estimate.mixedCurrency,
    };
  }

  /**
   * Render an estimate as CSV (Excel-friendly).
   *
   * Tax is per line, because a product can carry its own rate — the rate and
   * amount are columns so a mixed-rate quote reconciles line by line rather
   * than presenting one total nobody can check.
   *
   * Headers, separator and decimal mark follow the user's language (or their
   * choice in the Export dialog): English headers split on commas opened in a
   * Spanish or German Excel as one column of text.
   */
  buildEstimateCsv(estimate: ProductEstimate): string {
    const {delimiter, decimal} = this.estimateCsvFormat();
    const label = (key: string) => this.strings.getString(key);
    const num = (value: number) => {
      const text = (value || 0).toFixed(2);
      return decimal === '.' ? text : text.replace('.', decimal);
    };
    // A name is typed by a person, and a spreadsheet runs a cell starting
    // with one of these as a formula.
    const text = (value: string) => /^[=+\-@\t\r]/.test(value || '') ? `'${value}` : (value || '');
    const esc = (v: any) => `"${('' + (v == null ? '' : v)).replace(/"/g, '""')}"`;
    const row = (cells: any[]) => cells.map(esc).join(delimiter);
    const rows: string[] = [];
    rows.push(row([label('product_name'), label('area'), label('area_unit'), label('perimeter'),
      label('distance_unit'), label('shapes'), label('cost'), `${label('tax_rate')} (%)`,
      label('tax'), label('currency')]));
    estimate.lines.forEach(l => {
      rows.push(row([
        text(l.name),
        l.areaQty ? num(l.areaQty) : '',
        l.areaQty ? text(l.areaUnit) : '',
        l.distanceQty ? num(l.distanceQty) : '',
        l.distanceQty ? text(l.distanceUnit) : '',
        l.count,
        num(l.cost),
        ('' + (l.taxRate || 0)).replace('.', decimal),
        num(l.tax),
        l.currency
      ]));
    });
    rows.push('');
    Object.keys(estimate.subtotals).forEach(c => {
      const subtotal = estimate.subtotals[c];
      const tax = estimate.taxes[c] || 0;
      rows.push(row([label('subtotal'), '', '', '', '', '', num(subtotal), '', '', c]));
      if (tax > 0) {
        rows.push(row([label('tax'), '', '', '', '', '', '', '', num(tax), c]));
        rows.push(row([label('total'), '', '', '', '', '', num(subtotal + tax), '', '', c]));
      }
    });
    return rows.join('\r\n');
  }

  /**
   * The estimate CSV's separator and decimal mark: the ones last chosen in the
   * Export dialog, or else the convention of the user's language, which is
   * what that dialog starts from.
   */
  estimateCsvFormat(): {delimiter: string, decimal: string} {
    const language = ('' + (this.strings.getLanguage() || navigator.language || 'en')).toLowerCase();
    const commaDecimal = ['es', 'de', 'fr', 'pt', 'ru', 'in', 'id', 'it', 'nl']
      .some(code => language.startsWith(code));
    // Read raw: the settings reader turns a saved tab into the number 0.
    const saved = (key: string, allowed: string[]): string | null => {
      try {
        const value = localStorage.getItem(key);
        return allowed.includes(value) ? value : null;
      } catch {
        return null;
      }
    };
    return {
      delimiter: saved('csvDelimiter', [',', ';', '\t']) ?? (commaDecimal ? ';' : ','),
      decimal: saved('csvDecimalSeparator', ['.', ',']) ?? (commaDecimal ? ',' : '.'),
    };
  }

  /** "<project> - <Estimate>.csv" in the user's language, not "estimate.csv" for every quote. */
  estimateCsvFileName(): string {
    const name = [this.projectService.currentProject?.name?.trim(), this.strings.getString('estimate')]
      .filter(Boolean).join(' - ');
    return `${name.replace(/[\\/:*?"<>|]+/g, '_')}.csv`;
  }
}
